import styled from "styled-components";
import {
  border,
  radius,
  spacing,
  status,
  surface,
  text,
  typography,
} from "../../theme/tokens";

/**
 * Matter Work panel chrome — the WHG Docs twin of the Salesforce
 * matter page's work rail. Rows reuse the AI panel's inbox-list
 * vocabulary (InboxItemRow etc.); what lives here is the work-specific
 * dressing: lane headers, staleness chips, the expanded item's thread,
 * and the verb bar.
 */

export const WorkLaneHeader = styled.h4`
  margin: ${spacing[4]} 0 ${spacing[1]} 0;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${text.muted};

  &:first-of-type {
    margin-top: ${spacing[2]};
  }
`;

/** Amber = aging, red = stale/overdue; mirrors the Salesforce rail. */
export const WorkStaleChip = styled.span`
  display: inline-block;
  padding: 0 ${spacing[1.5]};
  margin-right: ${spacing[1]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  border-radius: ${radius.sm};
  background: ${({ $tone }) =>
    $tone === "red" ? status.failed.bg : status.review.bg};
  color: ${({ $tone }) =>
    $tone === "red" ? status.failed.fg : status.review.fg};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "red" ? status.failed.border : status.review.border};
`;

export const WorkItemSubjectButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  color: ${text.primary};

  &:hover {
    color: #0c53bb;
  }
`;

const richTextBlockStyles = `
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;

  p {
    margin: 0 0 ${spacing[2]} 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 0 0 ${spacing[2]} 0;
    padding-left: ${spacing[4]};
  }

  h1,
  h2,
  h3 {
    margin: ${spacing[2]} 0 ${spacing[1]} 0;
    font-weight: ${typography.weight.semibold};
    color: inherit;
  }

  h1 {
    font-size: ${typography.size.base};
  }

  h2,
  h3 {
    font-size: ${typography.size.sm};
  }

  blockquote {
    margin: 0 0 ${spacing[2]} 0;
    padding-left: ${spacing[3]};
    border-left: 2px solid ${border.light};
    color: ${text.secondary};
  }

  a {
    color: #0c53bb;
  }
`;

export const WorkItemInstruction = styled.div`
  margin: ${spacing[2]} 0 0 0;
  font-size: ${typography.size.sm};
  color: ${text.secondary};
  ${richTextBlockStyles}
`;

export const WorkThreadList = styled.div`
  margin-top: ${spacing[2]};
  padding-left: ${spacing[3]};
  border-left: 2px solid ${border.light};
`;

export const WorkThreadEntry = styled.div`
  & + & {
    margin-top: ${spacing[2]};
  }
`;

export const WorkThreadAuthor = styled.div`
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  color: ${text.secondary};
`;

export const WorkThreadBody = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.primary};
  ${richTextBlockStyles}
`;

export const WorkVerbBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[1.5]};
  margin-top: ${spacing[2]};
`;

export const WorkVerbButton = styled.button`
  padding: ${spacing[1]} ${spacing[2.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  color: ${({ $primary }) => ($primary ? "#FFFFFF" : text.secondary)};
  background: ${({ $primary }) => ($primary ? "#0C53BB" : surface.surface)};
  border: 1px solid ${({ $primary }) => ($primary ? "#0C53BB" : border.default)};
  border-radius: ${radius.sm};
  cursor: pointer;
  transition: opacity 120ms ease, background 120ms ease;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** The one-line state a snoozed/outside/blocked item shows —
 * "Snoozed until 9/20 — asked client for statement", "Coming up —
 * after Order Exam is approved", the doorway's own outsideLabel
 * (Matthew, 9/4). Sits with the rest of the meta, not off on its own. */
export const WorkStateLine = styled.div`
  margin-top: ${spacing[1]};
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

/* ------------------------------------------------------------------ *
 * Progress — the "sub-chips" build (Matthew, 9/9): what has happened
 * on an item (Called client 9/8 · Requested (Mednick) 9/2 · expected
 * 9/20), what should happen next, and the one-click way to log the
 * next step. Chips read left to right in time order.
 * ------------------------------------------------------------------ */

export const WorkStepChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[1]};
  margin-top: ${spacing[1]};
`;

/** A logged step. `$tone` "done" (quiet green) for Received, "flag"
 * (amber) for a pending follow-up, "over" (red) for an overdue
 * follow-up or a missing document, else neutral. */
export const WorkStepChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 0 ${spacing[1.5]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  line-height: 18px;
  border-radius: ${radius.sm};
  white-space: nowrap;
  background: ${({ $tone }) =>
    $tone === "over"
      ? status.failed.bg
      : $tone === "flag"
        ? status.review.bg
        : $tone === "done"
          ? status.ready.bg
          : status.neutral.bg};
  color: ${({ $tone }) =>
    $tone === "over"
      ? status.failed.fg
      : $tone === "flag"
        ? status.review.fg
        : $tone === "done"
          ? status.ready.fg
          : text.secondary};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "over"
        ? status.failed.border
        : $tone === "flag"
          ? status.review.border
          : $tone === "done"
            ? status.ready.border
            : border.light};
`;

/** Every condition the item spans, one chip each — the subject line
 * only has room for three ("+1 more"). Names wrap rather than clip. */
export const WorkConditionChips = styled(WorkStepChips)`
  margin-top: ${spacing[1.5]};
  font-size: ${typography.size.sm};

  strong {
    font-weight: ${typography.weight.semibold};
    color: ${text.secondary};
    margin-right: ${spacing[0.5]};
  }

  ${WorkStepChip} {
    white-space: normal;
  }
`;

/** "Next: talk to the client about the statement" — the card tells
 * the holder what to do, not only what has been done. */
export const WorkNextLine = styled.div`
  margin-top: ${spacing[1.5]};
  font-size: ${typography.size.sm};
  color: ${text.primary};

  strong {
    font-weight: ${typography.weight.semibold};
    color: ${text.secondary};
    margin-right: ${spacing[1]};
  }
`;

/** The "mark progress" row: one dashed pill per suggested step, plus
 * Follow-up… and Other…. Click → a small popover, not a modal. */
export const WorkStepButton = styled.button`
  padding: 0 ${spacing[2]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.medium};
  line-height: 20px;
  color: ${text.secondary};
  background: ${surface.surface};
  border: 1px dashed ${border.default};
  border-radius: 999px;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease;

  &:hover:not(:disabled) {
    color: #0c53bb;
    border-color: #0c53bb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const WorkStepPopoverBody = styled.div`
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

export const WorkStepPopoverFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[1.5]};
  margin-top: ${spacing[1]};
`;

export const WorkPanelHint = styled.p`
  margin: ${spacing[3]} 0 0 0;
  font-size: ${typography.size.sm};
  color: ${text.muted};
  line-height: 1.5;
`;

/* ------------------------------------------------------------------ *
 * Work Items v2 (Matthew, 11/2): the route under the subject, the ask
 * chip ("Sign" / "Review") that lets a court be scanned for sign-offs,
 * the attachment list, and who is following.
 * ------------------------------------------------------------------ */

export const WorkAskChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 ${spacing[1.5]};
  margin-right: ${spacing[1]};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  line-height: 18px;
  border-radius: 999px;
  background: #573a75;
  color: #fff;
  white-space: nowrap;
  vertical-align: middle;
`;

export const WorkRouteLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px ${spacing[1]};
  margin-top: ${spacing[1]};
  font-size: ${typography.size.xs};
  color: ${text.muted};
`;

/** One stop on the route. `$state` done | current | next | later | waiting. */
export const WorkRouteStop = styled.span`
  color: ${({ $state }) =>
    $state === "current" ? "#573a75" : $state === "done" ? "#a8a8a8" : text.muted};
  font-weight: ${({ $state }) =>
    $state === "current" ? typography.weight.semibold : typography.weight.regular};
  text-decoration: ${({ $state }) => ($state === "done" ? "line-through" : "none")};
  white-space: nowrap;
`;

export const WorkRouteArrow = styled.span`
  color: #c4c4c4;
`;

export const WorkAttachmentList = styled.ul`
  list-style: none;
  margin: ${spacing[2]} 0 0;
  padding: ${spacing[1.5]} ${spacing[2]};
  background: #f7f5fa;
  border-radius: ${radius.sm};
  font-size: ${typography.size.sm};
`;

export const WorkAttachmentRow = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${spacing[2]};
  padding: 2px 0;
`;

export const WorkAttachmentMeta = styled.span`
  font-size: ${typography.size.xs};
  color: ${text.muted};
  white-space: nowrap;
`;

export const WorkFollowingLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
  margin-top: ${spacing[1]};
  font-size: ${typography.size.xs};
  color: ${text.muted};
`;
